import { addSeconds, format, isFuture } from 'date-fns';
import { AppThunk } from 'store';

import {
  ChatMessageFormat,
  FakeChatMessageFormat,
} from 'types/enums/ChatMessageFormat';
import { ChatGift } from 'types/interfaces/chat/ChatGift';
import { ChatPrivatePhoto } from 'types/interfaces/chat/ChatPrivatePhoto';
import { ChatSticker } from 'types/interfaces/chat/ChatSticker';
import { User } from 'types/interfaces/user/User';

import { Dialog, DialogsApi, Message } from 'api/DialogsApi';
import { addUTCToDate, getUTCDate, parseDateToSafari } from 'helpers/dates';
import { useMatchPopupAnalytics } from 'hooks/analytics/useMatchPopupAnalytics';

import {
  addDialog,
  addDialogs,
  addMessage,
  addMessages,
  readLastDialogIncomeMessage,
  setDialogs,
  setMessages,
  setMessagesLoading,
  updateDialogWithNewMessage,
} from './messengerSlice';

export const fetchDialogs = (): AppThunk => async (dispatch, getState) => {
  const { dialogs } = getState().messenger;

  const dialogsResponse = await DialogsApi.fetchDialogs();

  const a = dialogs.data.reduce<Record<string, Dialog>>((acc, item) => {
    return {
      ...acc,
      [item.contact.id]: item,
    };
  }, {});

  dispatch(
    setDialogs({
      ...dialogsResponse,
      data: dialogsResponse.data.concat(
        Object.values(a).filter(
          (dialog) => dialog.last_message.format === FakeChatMessageFormat.Match
        )
      ),
    })
  );
};

export const fetchMoreDialogs = (): AppThunk => async (dispatch, getState) => {
  const nextUrl = getState().messenger.dialogs.next;

  const dialogsresponse = await DialogsApi.fetchDialogs(nextUrl);

  dispatch(addDialogs(dialogsresponse));
};

export const readMessages = (payload: { contactId: number }) => () => {
  DialogsApi.fetchMessages(payload.contactId);
};

export const fetchMessages =
  (payload: { contactId: number }): AppThunk =>
  async (dispatch) => {
    dispatch(setMessagesLoading(true));

    const messagesResponse = await DialogsApi.fetchMessages(payload.contactId);

    dispatch(
      setMessages({
        messages: messagesResponse.messages,
        contactId: messagesResponse.contact.id,
        contact: messagesResponse.contact,
        next: messagesResponse.next,
        isMatched: Boolean(messagesResponse.is_matched),
      })
    );
    dispatch(readLastDialogIncomeMessage(messagesResponse.contact.id));
    dispatch(setMessagesLoading(false));
  };

export const fetchMoreMessages =
  (payload: { contactId: number }): AppThunk =>
  async (dispatch, getState) => {
    const { contactId } = payload;

    const nextUrl = getState().messenger.messages[contactId].next;

    const messagesResponse = await DialogsApi.fetchMoreMessages(
      nextUrl as unknown as string
    );

    dispatch(
      addMessages({
        messages: messagesResponse.messages,
        contactId,
        next: messagesResponse.next,
      })
    );
  };

export const sendTextMessage =
  (payload: {
    contactId: number;
    body: string;
    format:
      | ChatMessageFormat.Text
      | ChatMessageFormat.MatchIcebreaker
      | ChatMessageFormat.Icebreaker
      | ChatMessageFormat.Video;
  }): AppThunk =>
  async (dispatch) => {
    const { trackPopupClick } = useMatchPopupAnalytics();

    try {
      const { data } = await DialogsApi.sendTextMessage({
        ...payload,
        format: payload.format,
      });

      if (data.message.id) {
        dispatch(fetchMessages({ contactId: payload.contactId }));
      }

      if (payload.format === ChatMessageFormat.MatchIcebreaker) {
        trackPopupClick({
          contactId: payload.contactId,
          messageId: data.message.id,
        });
      }
    } catch (err) {
      if (payload.format === ChatMessageFormat.MatchIcebreaker) {
        trackPopupClick({
          contactId: payload.contactId,
        });
      }

      throw err;
    }
  };

export const sendTextMessageWithInsert =
  (payload: {
    messageId: number;
    contactId: number;
    body: string;
    format:
      | ChatMessageFormat.Text
      | ChatMessageFormat.MatchIcebreaker
      | ChatMessageFormat.Icebreaker
      | ChatMessageFormat.Video;
  }): AppThunk =>
  async (dispatch, getState) => {
    const newMessage = {
      body: payload.body,
      contact_id: payload.contactId,
      deleted_at: null,
      format: payload.format,
      id: payload.messageId,
      is_incoming: false,
      is_paid: false,
      is_failed: false,
      read_at: null,
      response_to: null,
      sent_at: format(getUTCDate(Date.now()), 'yyyy-MM-dd HH:mm:ss'),
      user_id: getState().auth.user?.id ?? -1,
    } as Message;

    dispatch(
      addMessage({
        message: newMessage,
        contactId: payload.contactId,
      })
    );
    dispatch(updateDialogWithNewMessage(newMessage));

    await dispatch(
      sendTextMessage({
        contactId: payload.contactId,
        body: payload.body,
        format: payload.format,
      })
    );
  };

export const sendPhotoMessage =
  (payload: {
    messageId: number;
    contactId: number;
    photo: ChatPrivatePhoto;
  }): AppThunk =>
  async (dispatch, getState) => {
    const newMessage = {
      contact_id: payload.contactId,
      deleted_at: null,
      format: ChatMessageFormat.Photo,
      id: payload.messageId,
      is_incoming: false,
      read_at: null,
      response_to: null,
      sent_at: format(getUTCDate(Date.now()), 'yyyy-MM-dd HH:mm:ss'),
      photo: payload.photo,
      user_id: getState().auth.user?.id ?? -1,
    } as Message;

    dispatch(
      addMessage({
        message: newMessage,
        contactId: payload.contactId,
      })
    );
    dispatch(updateDialogWithNewMessage(newMessage));

    await DialogsApi.sendPhotoMessage({
      contactId: payload.contactId,
      photoId: payload.photo.id,
      format: ChatMessageFormat.Photo,
    });
  };

export const sendStickerMessage =
  (payload: {
    messageId: number;
    contactId: number;
    sticker: ChatSticker;
  }): AppThunk =>
  async (dispatch, getState) => {
    const newMessage = {
      contact_id: payload.contactId,
      deleted_at: null,
      format: ChatMessageFormat.Sticker,
      id: payload.messageId,
      is_incoming: false,
      read_at: null,
      response_to: null,
      sent_at: format(getUTCDate(Date.now()), 'yyyy-MM-dd HH:mm:ss'),
      user_id: getState().auth.user?.id ?? -1,
      sticker: payload.sticker,
    } as Message;

    dispatch(
      addMessage({
        message: newMessage,
        contactId: payload.contactId,
      })
    );

    await DialogsApi.sendStickerMessage({
      format: ChatMessageFormat.Sticker,
      contactId: payload.contactId,
      stickerId: payload.sticker.id,
    });
  };

export const sendGiftMessage =
  (payload: { contactId: number; gift: ChatGift }): AppThunk =>
  async () => {
    await DialogsApi.sendGiftMessage({
      contactId: payload.contactId,
      giftId: payload.gift.id,
    });
  };

export const sendGiftMessageWithInsert =
  (payload: {
    messageId: number;
    contactId: number;
    gift: ChatGift;
  }): AppThunk =>
  async (dispatch, getState) => {
    const newMessage = {
      id: payload.messageId,
      format: ChatMessageFormat.Gift,
      contact_id: payload.contactId,
      deleted_at: null,
      is_incoming: false,
      read_at: null,
      response_to: null,
      sent_at: format(getUTCDate(Date.now()), 'yyyy-MM-dd HH:mm:ss'),
      user_id: getState().auth.user?.id ?? -1,
      gift: payload.gift,
    } as Message;

    dispatch(
      addMessage({
        message: newMessage,
        contactId: payload.contactId,
      })
    );

    await dispatch(
      sendGiftMessage({
        contactId: payload.contactId,
        gift: payload.gift,
      })
    );
  };

export const addNewMessageFromSocket =
  (payload: { message: Message; contactId: number }): AppThunk =>
  (dispatch, getState) => {
    const { dialogs } = getState().messenger;

    dispatch(addMessage(payload));

    const hasDialogWith = (contactId: number) => (dialog: Dialog) =>
      dialog.contact.id === contactId;

    if (dialogs.data.some(hasDialogWith(payload.contactId))) {
      dispatch(updateDialogWithNewMessage(payload.message));
    } else {
      dispatch(fetchDialogs());
    }
  };

export const addNewMatchDialogFromSocket =
  (payload: { preview: string; contact: User }): AppThunk =>
  (dispatch, getState) => {
    const { contact, preview } = payload;
    const { dialogs } = getState().messenger;

    const hasDialogWith = (contactId: number) => (dialog: Dialog) =>
      dialog.contact.id === contactId;

    if (!dialogs.data.some(hasDialogWith(payload.contact.id))) {
      const newMessage = {
        id: 1,
        format: FakeChatMessageFormat.Match,
        body: preview,
        contact_id: contact.id,
        deleted_at: null,
        is_incoming: false,
        read_at: null,
        response_to: null,
        sent_at: format(getUTCDate(Date.now()), 'yyyy-MM-dd HH:mm:ss'),
        user_id: getState().auth.user?.id ?? -1,
      } as Message;

      dispatch(
        addDialog({
          contact,
          last_message: newMessage,
          unread_count: 1,
        })
      );
    }
  };

export const removeExpireMatchDialogs =
  (): AppThunk => (dispatch, getState) => {
    const { dialogs } = getState().messenger;

    const updatedDialogsList = dialogs.data.filter((dialog) => {
      const parsedDate = new Date(
        addUTCToDate(parseDateToSafari(dialog.last_message.sent_at))
      );
      const isMatch =
        dialog.last_message.format === FakeChatMessageFormat.Match;
      return !isMatch || (isMatch && isFuture(addSeconds(parsedDate, 60)));
    });

    dispatch(
      setDialogs({
        ...dialogs,
        data: updatedDialogsList,
      })
    );
  };
